import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, redeemCode, redeemToken, requestLogin } from '../api';
import { useSession, useTenant } from '../app';
import { Kicker, Link } from '../components';
import { navigate } from '../router';

/**
 * Anmeldung ohne Passwort (Konzept 4.1): E-Mail eingeben → Link und Code kommen per Mail.
 * Lokal ohne Mailversand zeigt der Server den Link direkt an (devLink).
 */
export function LoginPage() {
  const tenant = useTenant();
  const session = useSession();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<{ devLink?: string; devCode?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submitEmail(event: FormEvent) {
    event.preventDefault();
    if (busy || !email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await requestLogin(email.trim());
      setSent({ ...(r.devLink ? { devLink: r.devLink } : {}), ...(r.devCode ? { devCode: r.devCode } : {}) });
    } catch {
      setError('Das hat nicht geklappt. Bitte versuch es gleich noch einmal.');
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault();
    if (busy || code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      await redeemCode(email.trim(), code);
      await session.refresh();
      navigate('/');
    } catch (e) {
      setError(e instanceof ApiError && e.status === 401 ? 'Der Code stimmt nicht oder ist abgelaufen. Fordere einfach einen neuen an.' : 'Das hat nicht geklappt. Bitte versuch es noch einmal.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page page--narrow">
      <div className="card card--login">
        <Kicker>{tenant.name}</Kicker>
        <h1 className="login__title">Anmelden</h1>
        {!sent ? (
          <form onSubmit={submitEmail} className="form">
            <p className="muted">Gib die E-Mail-Adresse ein, mit der du gekauft hast. Du bekommst einen Anmelde-Link – ganz ohne Passwort.</p>
            <div className="field">
              <label htmlFor="email">E-Mail-Adresse</label>
              <input id="email" type="email" inputMode="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="du@beispiel.de" autoFocus />
            </div>
            {error && <div className="error">{error}</div>}
            <button type="submit" className="button button--large" disabled={busy || !email.trim()}>
              Anmelde-Link schicken
            </button>
          </form>
        ) : (
          <form onSubmit={submitCode} className="form">
            <p>
              Wenn <strong>{email}</strong> bei uns bekannt ist, ist jetzt eine Mail mit deinem Anmelde-Link unterwegs. Der Link gilt 15 Minuten.
            </p>
            <p className="muted small">Mail auf einem anderen Gerät geöffnet? Dann gib hier den sechsstelligen Code aus der Mail ein.</p>
            <div className="field">
              <label htmlFor="code">Code aus der Mail</label>
              <input id="code" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="123456" className="input--code" />
            </div>
            {error && <div className="error">{error}</div>}
            <button type="submit" className="button button--large" disabled={busy || code.length !== 6}>
              Mit Code anmelden
            </button>
            <button type="button" className="link link--muted" onClick={() => setSent(null)}>
              Andere Adresse verwenden
            </button>
            {sent.devLink && (
              <div className="devnote">
                <strong>Nur lokal (kein Mailversand eingerichtet):</strong>{' '}
                <Link href={sent.devLink}>Anmelde-Link öffnen</Link>
                {sent.devCode && <> · Code {sent.devCode}</>}
              </div>
            )}
          </form>
        )}
        <p className="muted small login__hint">
          Noch keinen Zugang? Die Kurse findest du in der <Link href="/">Bibliothek</Link>.
        </p>
      </div>
    </main>
  );
}

/** /anmelden/<token>: Link aus der Mail einlösen und weiter in die Bibliothek. */
export function RedeemPage({ token }: { token: string }) {
  const session = useSession();
  const [state, setState] = useState<'working' | 'invalid'>('working');
  useEffect(() => {
    redeemToken(token)
      .then(async () => {
        await session.refresh();
        navigate('/', true);
      })
      .catch(() => setState('invalid'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (state === 'working') return <main className="page page--message" />;
  return (
    <main className="page page--message">
      <h1>Dieser Link ist abgelaufen</h1>
      <p className="muted">Anmelde-Links gelten 15 Minuten und nur einmal. Fordere einfach einen neuen an.</p>
      <p>
        <Link href="/anmelden" className="button">
          Neuen Link anfordern
        </Link>
      </p>
    </main>
  );
}
